import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CHAT_INPUT_MODES,
  CHAT_VIEW_STATES,
  ChatAPI,
  useChatSettings,
  useCurrentChat,
  useLocalMessages,
} from '@guuru/chat-web';
import events from '@guuru/events-web';
import { ChatMessageBar, InputSection, Widget } from '@guuru/react-chatwidget';
import LoadingView from '@guuru/react-loading';
import ChatAlert from '../../components/ChatAlert';
import ExpertProfileSheet from '../../components/ExpertProfileSheet';
import {
  initChatFlow,
  recoverChatConnection,
  runNextChatStep,
  startNewQuestion,
  submitChatText,
} from '../../flow/chatFlowCommands';
import useChatWrapper from '../../hooks/useChatWrapper';
import useIsMobile from '../../hooks/useIsMobile';
import ChatConnectionRetryCta from './ChatConnectionRetryCta';
import ChatPoweredBy from './ChatPoweredBy';
import ChatQuickQuestions from './ChatQuickQuestions';

const Chat = function () {
  const didMount = useRef(false);
  const isMobile = useIsMobile();
  const { partnerId, chatControlToken, chatId: chatIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const params = ChatAPI.storeRetrieveParams();
  const isCommunity = params.channel === 'community';
  const channel = isCommunity ? params.channel : 'chat';

  const {
    loading: isLoadingSettings,
    data: {
      chatSettings: {
        chatWidgetShowBotAvatar,
        chatWidgetCustomBotAvatar,
        chatWidgetCustomBotAvatarPhotoUrl,
        chatWidgetShowPoweredBy,
      } = {},
    } = {},
  } = useChatSettings();

  useEffect(() => {
    if (!didMount.current) {
      initChatFlow({
        partnerId,
        chatId: chatIdParam,
        chatControlToken,
        navigate,
        location,
        options: { channel },
      });

      didMount.current = true;
    }
  }, [navigate, location, chatControlToken, chatIdParam, partnerId, channel]);

  const handleConnectionError = () => {
    recoverChatConnection(partnerId);
  };

  const {
    chatId,
    inputMode,
    isConnectionError,
    isRunningStep,
    loading,
    viewState,
  } = useCurrentChat();
  const localMessages = useLocalMessages();
  useChatWrapper();

  const handleWidgetSubmitChatText = useCallback(
    (payload) => {
      submitChatText(partnerId, payload.chatId || chatId, payload);
    },
    [chatId, partnerId],
  );

  const displayPoweredBy = useMemo(() => {
    const viewStatesWherePoweredByIsVisible = [
      CHAT_VIEW_STATES.question,
      CHAT_VIEW_STATES.email,
      CHAT_VIEW_STATES.name,
    ];

    return (
      chatWidgetShowPoweredBy &&
      viewStatesWherePoweredByIsVisible.includes(viewState)
    );
  }, [chatWidgetShowPoweredBy, viewState]);

  const handleAskNewQuestion = useCallback(() => {
    events.userReloadChat();
    startNewQuestion(partnerId, navigate, location, { channel });
  }, [partnerId, navigate, location, channel]);

  const renderChatMessageBar = useCallback(
    ({ onAttachmentSubmit, onMessageSubmit }) => {
      const isHidden = () => {
        const isInputModeNone = inputMode === CHAT_INPUT_MODES.none;
        const isHiddenWhileRunningStep =
          isRunningStep &&
          [CHAT_VIEW_STATES.automation, CHAT_VIEW_STATES.question].includes(
            viewState,
          );
        return isInputModeNone || isHiddenWhileRunningStep;
      };

      if (isHidden()) {
        return null;
      }

      const handleSubmit = async (eventLabel, text, type) => {
        if (text.trim().length > 0) {
          onMessageSubmit();
          events.submit(eventLabel);
          submitChatText(partnerId, chatId, {
            text,
            inputMode,
            textType: type || 'text',
            skipAutomation: params.priority === 'bot',
          });
        }
      };

      return (
        <InputSection isUser>
          <ChatMessageBar
            isUser
            noFocus={params.noFocus === 'true' || isMobile}
            chatId={chatId}
            partnerId={partnerId}
            inputMode={inputMode}
            onSubmit={handleSubmit}
            onAttachmentSubmit={onAttachmentSubmit}
            isClosed={viewState === CHAT_VIEW_STATES.rate}
            isReviewingRate={viewState === CHAT_VIEW_STATES.rateBad}
            isFinished={
              inputMode === CHAT_INPUT_MODES.restart && !isRunningStep
            }
            onAskNewQuestion={handleAskNewQuestion}
          />
        </InputSection>
      );
    },
    [
      chatId,
      handleAskNewQuestion,
      inputMode,
      isRunningStep,
      isMobile,
      params.noFocus,
      params.priority,
      partnerId,
      viewState,
    ],
  );

  const getMods = () => {
    let mods = '';

    if (displayPoweredBy) {
      mods = `${mods} mod--powered-by`;
    }

    switch (viewState) {
      case CHAT_VIEW_STATES.chat:
        mods = `${mods} mod--chat`;
        break;
      case CHAT_VIEW_STATES.rate:
      case CHAT_VIEW_STATES.rateBad:
        mods = `${mods} mod--rate`;
        break;
      case CHAT_VIEW_STATES.connectingToExpert:
        mods = `${mods} mod--connecting`;
        break;
      case CHAT_VIEW_STATES.finished:
        mods = `${mods} mod--finished`;
        break;
      default:
        break;
    }

    return mods;
  };

  if (isLoadingSettings || loading || !didMount.current) {
    return <LoadingView />;
  }

  return (
    <Widget
      isUser
      chatId={chatId}
      localMessages={localMessages}
      viewState={viewState}
      inputMode={inputMode}
      showBotAvatar={chatWidgetShowBotAvatar}
      customBotAvatar={
        chatWidgetCustomBotAvatar ? chatWidgetCustomBotAvatarPhotoUrl : ''
      }
      showTermsAndConditions
      baseMods={getMods()}
      renderChatMessageBar={renderChatMessageBar}
      onRunNextStep={runNextChatStep}
      onSubmitChatText={handleWidgetSubmitChatText}
      isCommunity={isCommunity}
      alert={<ChatAlert />}
    >
      <ChatQuickQuestions />
      <ChatPoweredBy isUser visible={displayPoweredBy} partnerId={partnerId} />
      <ChatConnectionRetryCta
        visible={isConnectionError}
        action={handleConnectionError}
      />
      <ExpertProfileSheet chatId={chatId} partnerId={partnerId} />
    </Widget>
  );
};

export default Chat;
